import { Divider, IconButton, Stack } from '@mui/material';
import { isTiersProductVariant, isUsageProduct } from '@vy/services';
import type { UsageProductInput } from '@vy/types';
import { PRICING_MODELS, type PriceTier, type TiersProductVariant, type UsageProduct } from '@vy/types';
import { isNotNullOrUndefined, raise, removeInIndex } from '@vy/utils';
import React, { useCallback, useState } from 'react';
import { get, useFormContext } from 'react-hook-form';
import { twMerge } from 'tailwind-merge';
import { VayuTextField } from '../../../../../../components/design/vayu-theme-components/VayuTextField';
import { CloseIcon } from '../../../../../../components/icons';
import { usePlanCreationFormUtils } from '../../../../../../contexts/create-plan-context/CreatePlanProvider/hooks/use-plan-creation-form-utils';
import { useFormErrorNotificationContext } from '../../../../../../contexts/form-error-notification-context/form-error-notification-context';
import {
  getDefaultRevShareProductVariant,
  getDefaultTiersUsageProductVariant,
} from '../../../create-plan-page-consts/add-item-button-consts';
import { useChargeContext } from '../../charge-context';
import type { PlanInputForm } from '../../CreatePlanForm/create-plan-form-types/plan-input-form';
import type { CreatePlanTab } from '../../CreatePlanPageTabs/create-plan-page-types/create-plan-tab';
import { PlanChargesContainer } from '../../PlanChargesContainer';
import ProductDotMenuDropdown from '../../ProductDotMenu';
import { UsageProductGroupDialogForm } from './UsageProductGroupDialogForm';
import { UsageProductVariantHeader } from './UsageProductVariantHeader';
import { UsageProductVariantSummary } from './UsageProductVariantSummary';
import { UsageProductVariantTable } from './UsageProductVariantTable';

export const TiersUsageChargeEdit: React.FC = () => {
  const { item: product, itemIndex: productIndex, currency } = useChargeContext<UsageProduct>();
  const { registerErrorField } = useFormErrorNotificationContext<PlanInputForm, CreatePlanTab>();
  const {
    register,
    getValues,
    formState: { errors },
    setValue,
  } = useFormContext<PlanInputForm>();
  const [isProductGroupDialogOpen, setIsProductGroupDialogOpen] = useState(false);
  const { isFieldEditable } = usePlanCreationFormUtils();

  const productNameRegister = register(`usageProducts.${productIndex}.name`, {
    value: product.name,
  });

  registerErrorField(productNameRegister.name);

  const addVariant = () => {
    const isRevShare = isRevSharePricing(product);

    const newVariant = isRevShare ? getDefaultRevShareProductVariant() : getDefaultTiersUsageProductVariant();

    const updatedProductVariants = [...product.variants, newVariant];
    setValue(`usageProducts.${productIndex}.variants`, updatedProductVariants);
  };

  const onDelete = useCallback(() => {
    const updatedProducts = removeInIndex(getValues('usageProducts'), productIndex);
    setValue('usageProducts', updatedProducts);
  }, [getValues, productIndex, setValue]);

  const isGroupedProduct = product.variants.length > 1;

  const handleConvertToGroup = () => {
    setIsProductGroupDialogOpen(true);
  };

  function handleGroupDelete() {
    const [firstVariant] = product.variants;
    setValue(`usageProducts.${productIndex}.name`, undefined);
    setValue(`usageProducts.${productIndex}.variants`, [firstVariant]);
  }

  return (
    <div
      className={twMerge(
        'flex flex-col gap-y-4',
        isFieldEditable(`usageProducts.${productIndex}`) ? '' : 'pointer-events-none opacity-40',
      )}
    >
      <PlanChargesContainer
        key={product.id}
        className="relative px-0 pb-0"
        id={`create-product-${productIndex}`}
      >
        {isGroupedProduct && (
          <div className="flex max-w-xs px-8 pt-8">
            <VayuTextField
              placeholder="Text here"
              {...productNameRegister}
              label="Group Name"
              defaultValue={getValues(productNameRegister.name) ?? ''}
              helperText={get(errors, productNameRegister.name)?.message}
              error={isNotNullOrUndefined(get(errors, productNameRegister.name))}
              focused
            />
          </div>
        )}

        <div>
          <div className="absolute right-3 top-3">
            <Stack
              direction="row"
              divider={<Divider orientation="vertical" flexItem variant="middle" />}
              sx={{
                columnGap: 1
              }}
            >
              <ProductDotMenuDropdown
                isGrouped={isGroupedProduct}
                onCreateGroup={handleConvertToGroup}
                onDeleteGroup={handleGroupDelete}
              />

              <div>
                <IconButton
                  onClick={onDelete}
                  sx={{
                    width: '40px',
                    height: '40px',
                  }}
                >
                  <CloseIcon
                    color="#0D1B27DE"
                    style={{
                      transform: 'scale(0.7)',
                    }}
                  />
                </IconButton>
              </div>
            </Stack>
          </div>

          <UsageProductGroupDialogForm
            open={isProductGroupDialogOpen}
            onClose={() => setIsProductGroupDialogOpen(false)}
            onCreateClick={() => !isGroupedProduct && addVariant()}
            productIndex={productIndex}
          />

          <div className="mt-10">
            {product.variants.map((variant, variantIndex) => {
              if (!isTiersProductVariant(variant)) {
                raise('Invalid product variant type');
              }

              return (
                <div key={variant.id} className="even:bg-product-bk">
                  <UsageProductVariantHeader
                    productVariant={variant}
                    productVariantIndex={variantIndex}
                    productIndex={productIndex}
                    className="px-6"
                  />
                  <UsageProductVariantTable
                    key={variant.id}
                    isFlatRate={variant.pricing.priceConfiguration?.isFlatRate ?? false}
                    isChunked={variant.pricing.priceConfiguration?.isChunked ?? false}
                    tiers={getTiers(variant)}
                    productIndex={productIndex}
                    productVariantIndex={variantIndex}
                  />
                </div>
              );
            })}
          </div>
          <UsageProductVariantSummary
            editable
            product={product}
            productIndex={productIndex}
            isGroupedProduct={isGroupedProduct}
            currency={currency}
          />
        </div>
      </PlanChargesContainer>
    </div>
  );
};

function getTiers(productVariant: TiersProductVariant): Partial<PriceTier>[] {
  const tiers = productVariant.pricing?.priceConfiguration?.tiers as Partial<PriceTier>[];

  return tiers ?? ([] as Partial<PriceTier>[]);
}

function isRevSharePricing(product: UsageProduct): boolean {
  const pricing = product.variants.at(0)?.pricing;

  return (
    isUsageProduct<UsageProductInput>(product) &&
    pricing?.pricingModel === PRICING_MODELS.Tiers &&
    pricing.priceConfiguration?.isRevShare
  );
}
